import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import {
  formatReportCurrency,
  getStartAndEndDate,
  REPORT_DATE_FILTERS,
  REPORT_PAGINATION_LIMIT,
  REPORT_SKELETON_ROWS,
} from "../../../../../utils";
import AppPagination from "../../../../../Libraries/AppPagination";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const ProfitMargins = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, pageLoading, profitMargins, profitMarginsTotal } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDates = useRef(null);

  const fetchReport = useCallback((start, end, offset = 0) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "profit_margins",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
      limit: REPORT_PAGINATION_LIMIT,
      offset,
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_profit_margins_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("profit_margins");

  const onFetch = useCallback((start, end) => {
    currentDates.current = { start, end };
    fetchReport(start, end, 0);
  }, [fetchReport]);

  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(onFetch, "profit_margins");

  const handleNext = useCallback((offset) => {
    if (!currentDates.current) return;
    fetchReport(currentDates.current.start, currentDates.current.end, offset);
  }, [fetchReport]);

  const showNonIdeal = isYearlyFilter && !loading && profitMargins.length === 0;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="profit_margins_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.profit_margin}
        id="profit_margins"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="profit_margins_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="profit_margins_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "profit_margins" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || profitMargins.length > 0) && (
            <Box id="profit_margins_table_header" className="invoiceListTableHeader">
              <Box id="profit_margins_header_item_name" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="profit_margins_header_label_item_name" className="fontSize12 fontWeight500 newLightColor">{I18n.item_name}</ParagraphText>
              </Box>
              <Box id="profit_margins_header_units_sold" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="profit_margins_header_label_units_sold" className="fontSize12 fontWeight500 newLightColor">{I18n.units_sold}</ParagraphText>
              </Box>
              <Box id="profit_margins_header_total_sales" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="profit_margins_header_label_total_sales" className="fontSize12 fontWeight500 newLightColor">{I18n.total_sales}</ParagraphText>
              </Box>
              <Box id="profit_margins_header_total_cost" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="profit_margins_header_label_total_cost" className="fontSize12 fontWeight500 newLightColor">{I18n.total_cost}</ParagraphText>
              </Box>
              <Box id="profit_margins_header_gross_profit" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="profit_margins_header_label_gross_profit" className="fontSize12 fontWeight500 newLightColor">{I18n.gross_profit}</ParagraphText>
              </Box>
              <Box id="profit_margins_header_profit_margin" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="profit_margins_header_label_profit_margin" className="fontSize12 fontWeight500 newLightColor">{I18n.profit_margin}</ParagraphText>
              </Box>
            </Box>
          )}

          <Box id="profitMarginsScrollable" className={pageStyles.pageBody}>
            {loading ? (
              Array.from({ length: REPORT_SKELETON_ROWS }).map((_, i) => (
                <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={120} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={36} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={44} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              ))
            ) : profitMargins.length === 0 ? (
              <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={`${I18n.try_adjusting_filters} `}
                subTitleText={I18n.view_yearly_data}
                subTitleTextOnClick={handleResetFilter}
                subTitleTextId="profit_margins_filter_view_yearly"
              />
            ) : (
              <AppPagination
                data={{
                  handleNext,
                  pageLoad: pageLoading,
                  count: profitMarginsTotal,
                  list: profitMargins,
                  scrollableTarget: "profitMarginsScrollable",
                }}
              >
                {profitMargins.map(({ item_name, units_sold, total_sales, total_cost, gross_profit, profit_margin }, i) => (
                  <Box key={i} id={`profit_margins_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                    <Box id={`profit_margins_row_${i}_item_name`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`profit_margins_cell_${i}_item_name`} className="fontSize14 newFontColor">{item_name}</ParagraphText>
                    </Box>
                    <Box id={`profit_margins_row_${i}_units_sold`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`profit_margins_cell_${i}_units_sold`} className="fontSize14 newFontColor">{units_sold}</ParagraphText>
                    </Box>
                    <Box id={`profit_margins_row_${i}_total_sales`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`profit_margins_cell_${i}_total_sales`} className="fontSize14 newFontColor">{formatReportCurrency(total_sales)}</ParagraphText>
                    </Box>
                    <Box id={`profit_margins_row_${i}_total_cost`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`profit_margins_cell_${i}_total_cost`} className="fontSize14 newFontColor">{formatReportCurrency(total_cost)}</ParagraphText>
                    </Box>
                    <Box id={`profit_margins_row_${i}_gross_profit`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`profit_margins_cell_${i}_gross_profit`} className="fontSize14 newFontColor">{formatReportCurrency(gross_profit)}</ParagraphText>
                    </Box>
                    <Box id={`profit_margins_row_${i}_profit_margin`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`profit_margins_cell_${i}_profit_margin`} className="fontSize14 newFontColor">
                        {profit_margin != null ? `${profit_margin}%` : "—"}
                      </ParagraphText>
                    </Box>
                  </Box>
                ))}
              </AppPagination>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProfitMargins;
